import { useEffect, useState } from "react";

import axios from "axios";
import { FileText } from "lucide-react";

/**
 * A component that fetches an image with the Authorization header
 * and displays it.
 */
const SecureImage = ({ src, alt, className, fallbackIcon: FallbackIcon, onClick }) => {
  const [imgSrc, setImgSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let objectUrl = null;

    const fetchImage = async () => {
      if (!src) return;

      // If it's a base64 string, just use it directly
      if (src.startsWith("data:")) {
        setImgSrc(src);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const response = await axios.get(src, {
          headers: {
            Authorization: token ? `Bearer ${token}` : ""
          },
          responseType: "blob"
        });

        objectUrl = URL.createObjectURL(response.data);
        setImgSrc(objectUrl);
        setError(false);
      } catch (err) {
        console.error("Failed to load secure image:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchImage();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src]);

  if (loading) {
    return <div className={`${className} bg-slate-100 animate-pulse rounded-md`} />;
  }

  if (error || !imgSrc) {
    return (
      <div
        className={`${className} bg-slate-200 dark:bg-yale-blue-800 flex items-center justify-center rounded-md border text-slate-400`}
        onClick={onClick}
      >
        {FallbackIcon ? <FallbackIcon className="w-6 h-6" /> : <span className="text-[10px] text-center px-1">N/A</span>}
      </div>
    );
  }

  const isPdf = (src && src.startsWith("data:application/pdf")) || (src && src.toLowerCase().endsWith(".pdf"));

  if (isPdf) {
    return (
      <div
        className={`${className} flex flex-col items-center justify-center bg-slate-100 dark:bg-yale-blue-800 rounded-md border cursor-pointer`}
        onClick={onClick}
      >
        <FileText className="h-8 w-8 text-slate-400 mb-1" />
        <span className="text-[10px] text-yale-blue-500 font-medium px-2 text-center truncate w-full">Document</span>
      </div>
    );
  }

  return <img src={imgSrc} alt={alt} className={className} onClick={onClick} />;
};

export default SecureImage;
